import { ApduResponse, TapClientInterface } from '@iotize/tap/client/api';
import { NumberConverter, TapClient } from '@iotize/tap/client/impl';
import { ComProtocol } from '@iotize/tap/protocol/api';
import { Observable, Subject } from 'rxjs';

import { debug } from '../logger';
import { TapEvent } from './definitions';
import { ServiceCallRunner } from './service-call-runner';
import { ServiceContainer } from './service-container';
import { NFC_PAIRING_COMMAND } from './tap-config';
import { TapConfigurator } from './tap-configurator';
import { TapError } from './tap-errors';

const TAG = 'Device';
export interface DisconnectOptions {
  clearSession?: boolean;
  clearCache?: boolean;
}

/**
 *
 */
export class Tap {
  static TAG = 'Tap';

  protected _client: TapClientInterface;
  protected _lwm2m: ServiceCallRunner;

  private _events = new Subject<TapEvent<string>>();

  service: ServiceContainer;

  public get events(): Observable<TapEvent<string>> {
    return this._events.asObservable();
  }

  public get client(): TapClientInterface {
    return this._client;
  }

  public get lwm2m() {
    return this._lwm2m;
  }

  public set client(client: TapClientInterface) {
    this._client = client;
    this._lwm2m.client = client;
    // this._services.client = client;
  }

  constructor(client: TapClientInterface) {
    this._client = client;
    this._lwm2m = new ServiceCallRunner(this.client);
    this.service = new ServiceContainer(this._lwm2m);
    // 'service', (context) => new ServiceContainer(context.tap.lwm2m)
    // this._scramInterceptor = new ScramInterceptor(
    //     this._services.scram,
    //     this.apiConfig
    // );
    // this._client.addInterceptor(this._scramInterceptor);
    // this._initCache();
    // this._converterProvider = new ConverterProvider(PathToTypeMap, DefaultConverterMap);
  }

  notifyEvent<T extends string>(event: TapEvent<T>) {
    this._events.next(event);
  }

  static create(): Tap {
    return new Tap(TapClient.create());
  }

  static fromProtocol(protocol: ComProtocol): Tap {
    const client = TapClient.create();
    client.addComProtocol(protocol);
    return new Tap(client);
  }

  public get protocol() {
    return this.client.getCurrentProtocol();
  }

  /**
   * Connect with the given communication protocol
   * TODO what if there is already a connected communication protocol
   * @param protocol
   */
  public connect(protocol?: ComProtocol): Promise<void> {
    debug(TAG, 'connecting...');
    // this._clearCache();
    if (protocol) {
      this.client.addComProtocol(protocol, 'default');
    }
    return this._client.connect().toPromise();
  }

  /**
   * Disconnect device
   */
  public disconnect(): Promise<void> {
    debug(TAG, 'disconnecting...');
    // if (options.clearCache) {
    //     this.clearCache();
    // }
    // if (options.clearSession) {
    //     this.clearSession();
    // }
    return this._client.disconnect().toPromise();
  }

  public isConnected(): boolean {
    if (!this._client) {
      return false;
    }
    return this._client.isConnected();
  }

  /**
   * Configure current tap instance thanks to a TapConfigurator
   * @param configurator
   */
  public configure<DataType>(
    configurator: TapConfigurator<DataType>,
  ): Promise<DataType> {
    return configurator.configure(this).toPromise() as Promise<DataType>;
  }

  /**
   *
   * @param protocol
   */
  public useComProtocol(protocol: ComProtocol | string): this {
    this.client.useComProtocol(protocol);
    return this;
  }

  /**
   * Send an NFC pairing request
   */
  public async nfcPairing(): Promise<Uint8Array> {
    const comProtocol = await this.client.getCurrentProtocol();
    const response = await comProtocol.send(NFC_PAIRING_COMMAND).toPromise();
    if (!response || response.length !== 2) {
      throw TapError.nfcPairingError(
        `Tap responded with a frame size of ${
          response?.length || 0
        } byte(s) but expecting 2 bytes.`,
      );
    }
    const statusCode = NumberConverter.uint16().decode(response);
    if (statusCode !== ApduResponse.Status.OK) {
      throw TapError.nfcPairingError(
        `Tap responded with an invalid APDU response status 0x${statusCode.toString(
          16,
        )}.`,
      );
    }
    return response;
  }

  // /**
  //  * @experimental
  //  */
  // get lwm2m(): Lwm2mInterface {
  //     return this._lwm2m;
  // }

  // /**
  //  * @deprecated use lwm2m object instead ?
  //  */
  // public lwm2mCall(...calls: ServiceCallType[]) {
  //     if (calls.length > 1) {
  //         throw new Error("Multiple calls are not implemented yet");
  //     }
  //     let args = AbstractService.callToCommandArgs(calls[0], this.apiConfig);
  //     var response = this.client.command<string>(args[0], args[1]).toPromise();
  //     return response;
  // }

  // /**
  //  * Initialize device cache
  //  */
  // protected _initCache() {
  //     if (!this._sessionState$) {
  //         this._sessionState$ = new BehaviorSubject<SessionState>(
  //             Tap.INITIAL_SESSION_STATE
  //         );
  //     }
  //     this.clearCache();
  //     this.clearSession();
  // }

  // /**
  //  * Clear cache
  //  */
  // public clearCache() {
  //     this._interfaceLockOptions = undefined;
  //     this._scramInterceptor.ivSupported = true;
  // }

  // /**
  //  * Clear session encryption keys
  //  */
  // public clearSession() {
  //     this.sessionKey = undefined;
  // }

  // public async getSecurityOptions(): Promise<SecurityOptions> {
  //     this._interfaceLockOptions = undefined;
  //     const options = await this.getLockInfo();
  //     const securityOptions = {
  //         disableFactoryResetByResource: options.disableResourceFactoryReset,
  //         disableLoginWithUID: options.disableLoginWithUID,
  //         disableFactoryResetByHardware: options.factoryReset,
  //         scramActivated: options.scramActivated,
  //         hashPassword: options.hashPassword
  //     };
  //     return securityOptions;
  // }
}
